/**
 * DD outcome helpers — derive the before/after score snapshots from real
 * score_event history (no new scoring engine). "Before" = the score as of DD
 * start; "after" = the score as of DD conclusion. Phase A uses this to validate
 * that the before/after delta is real signal (not LLM noise) on actual history
 * before the Phase B re-score engine is built.
 */

import type { ScoreEventReadModel } from '@/lib/supabase/edge-functions';

/** The DD UI's score snapshot. `risk` is a risk LEVEL (lower = better) — the DD
 *  UI colours it green when low — so it inverts the canonical fourAxis.risk
 *  (which is higher = lower-risk = better). */
export interface DDScoreSnapshot {
  overall: number;
  confidence: number;
  risk: number;
}

type EventLike = Pick<ScoreEventReadModel, 'computedAt' | 'composite'>;

/**
 * The latest score_event at or before `atIso`, mapped to the DD snapshot shape.
 * `events` must be ascending by computedAt. Returns null when there is no event
 * on or before that instant (or no timestamp).
 */
export function scoreSnapshotAt(events: EventLike[], atIso: string | null): DDScoreSnapshot | null {
  if (!atIso || events.length === 0) return null;
  const at = new Date(atIso).getTime();
  if (Number.isNaN(at)) return null;

  let chosen: EventLike | null = null;
  for (const e of events) {
    if (new Date(e.computedAt).getTime() <= at) chosen = e;
    else break; // ascending — nothing further qualifies
  }
  if (!chosen) return null;
  return snapshotFromComposite(chosen.composite);
}

/**
 * DD Phase B — the "after" snapshot with an explicit pin. When the DD-conclude
 * rescore ran, dd_reports.score_event_end_id names the exact event; prefer it
 * (immune to later rescores landing between conclusion and read). Falls back to
 * temporal derivation when there is no pin or the pinned event isn't in the
 * fetched history (RLS gap / GDPR-deleted event → column is ON DELETE SET NULL,
 * but the read stays defensive).
 */
export function scoreSnapshotPinned(
  events: (EventLike & { id?: string })[],
  pinnedEventId: string | null,
  fallbackAtIso: string | null,
): DDScoreSnapshot | null {
  if (pinnedEventId) {
    const pinned = events.find((e) => e.id === pinnedEventId);
    if (pinned) return snapshotFromComposite(pinned.composite);
  }
  return scoreSnapshotAt(events, fallbackAtIso);
}

/** Map a score_event composite to the DD UI snapshot (risk inverted to a level). */
function snapshotFromComposite(c: EventLike['composite']): DDScoreSnapshot {
  return {
    overall: Math.round(c.finalScore),
    confidence: Math.round(c.fourAxis.evidenceQuality),
    risk: Math.round(100 - c.fourAxis.risk),
  };
}

/** Whole-day duration between two ISO timestamps (min 1 when same-day). null if either missing. */
export function durationDays(startIso: string | null, endIso: string | null): number | null {
  if (!startIso || !endIso) return null;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  return Math.max(1, Math.round((end - start) / 86_400_000));
}

type RiskLike = { severity?: string | null };

/**
 * Real "flagged issues" count for a completed DD: the number of HIGH-severity
 * risks on the as-of-conclusion score_event. (Concern-category dd_notes would
 * augment this, but the shared-DD subsystem is not yet DB-persisted — see the
 * C.1 plan's "Blocked" section — so v1 is risks-only.)
 */
export function countHighSeverityRisks(risks: RiskLike[] | null | undefined): number {
  if (!Array.isArray(risks)) return 0;
  return risks.filter((r) => r?.severity === 'high').length;
}

/**
 * Honest data-room indicator for a completed DD — real document count + the
 * conclusion date — replacing the previously fabricated "v2.3 (Final)" string.
 * Returns undefined when there is nothing real to show.
 */
export function dataRoomSummary(
  documentsReviewed: number | null | undefined,
  concludedIso: string | null,
): string | undefined {
  const n = typeof documentsReviewed === 'number' && documentsReviewed > 0 ? documentsReviewed : 0;
  if (n === 0) return undefined;
  const noun = n === 1 ? 'document' : 'documents';
  let when = '';
  if (concludedIso) {
    const d = new Date(concludedIso);
    if (!Number.isNaN(d.getTime())) {
      when = ` · as of ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
  }
  return `${n} ${noun} reviewed${when}`;
}
